import { getConfig, getActiveSessionManager } from '@bounded-sh/core';
import { setAuthLoading } from '../global';
import { adoptSessionProvider } from './session-provider';
import { getPlatform } from '../platform';
import { hasTurnkeyWallet, signAndSubmitTransactionWithTurnkey, signMessageWithTurnkey, signTransactionWithTurnkey, signTransactionV1WithTurnkey, } from './turnkey-signer-bridge';
// Same message the email provider uses when a hosted (OIDC) login has no
// embedded wallet — enable `auth.wallets` in policy to get one.
const OIDC_NO_WALLET = 'This session has no Turnkey wallet. Sign in again to provision the default ' +
    'embedded wallet, or check whether the app explicitly sets auth.wallets:false.';
// PKCE material is stored PER ATTEMPT under a state-derived key
// (`bounded.oidc.pkce.<state>`), never one fixed key. Two overlapping logins in the
// same tab (redirect + popup, or two popups) each own a distinct record, so a second
// attempt cannot clobber the first's verifier/state/redirectUri.
const PKCE_STORE_PREFIX = 'bounded.oidc.pkce';
function pkceKey(state) {
    return `${PKCE_STORE_PREFIX}.${state}`;
}
const ISSUER_SESSION_KIND_KEY = 'bounded_issuer_session_kind';
// Active session store (Web on web, RN on React Native) — selected by config,
// not by `typeof window` (truthy on RN). OIDC redirect/popup is web-only, but the
// session it mints is read back through this same selector everywhere.
const sessionManager = getActiveSessionManager;
function hasDOM() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}
function decodeJwt(token) {
    try {
        let b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const pad = b64.length % 4;
        if (pad === 2)
            b64 += '==';
        else if (pad === 3)
            b64 += '=';
        return JSON.parse(getPlatform().atob(b64));
    }
    catch (_a) {
        return {};
    }
}
// A minimal AuthProvider so getSession()/logout() work for OIDC sessions, mirroring email.
// Turnkey supplies the default Solana wallet and supports raw message and transaction
// signatures through the Bounded signer window.
const oidcProviderSingleton = {
    async login() {
        const idToken = sessionManager().getIdToken();
        return idToken ? userFromIdToken(idToken) : null;
    },
    async getUser() {
        const idToken = sessionManager().getIdToken();
        return idToken ? userFromIdToken(idToken) : null;
    },
    async logout() { await sessionManager().clearSession(); },
    async signMessage(message) {
        if (!hasTurnkeyWallet())
            throw new Error(OIDC_NO_WALLET);
        return signMessageWithTurnkey(message);
    },
    async signTransaction(transaction) {
        if (!hasTurnkeyWallet())
            throw new Error(OIDC_NO_WALLET);
        return signTransactionWithTurnkey(transaction);
    },
    async signTransactionV1(transaction) {
        if (!hasTurnkeyWallet())
            throw new Error(OIDC_NO_WALLET);
        return signTransactionV1WithTurnkey(transaction);
    },
    supportedTransactionVersions() {
        return hasTurnkeyWallet() ? ['legacy', 0, 1] : ['legacy', 0];
    },
    async signAndSubmitTransaction(transaction, feePayer) {
        if (!hasTurnkeyWallet())
            throw new Error(OIDC_NO_WALLET);
        return signAndSubmitTransactionWithTurnkey(transaction, feePayer);
    },
};
function userFromIdToken(idToken) {
    const c = decodeJwt(idToken);
    const id = c['custom:userId'] || c.sub || c['custom:walletAddress'] || '';
    return {
        id,
        address: (c['custom:walletProvider'] === 'turnkey' && c['custom:walletAddress'] || null),
        evmAddress: null,
        email: (c.email || null), provider: oidcProviderSingleton,
    };
}
function tokenField(data, snake, camel) {
    var _a;
    const value = (_a = data === null || data === void 0 ? void 0 : data[snake]) !== null && _a !== void 0 ? _a : data === null || data === void 0 ? void 0 : data[camel];
    return typeof value === 'string' ? value : '';
}
function boundedRefreshTokenField(data) {
    for (const key of ['bounded_refresh_token', 'boundedRefreshToken', 'refreshToken']) {
        const value = data === null || data === void 0 ? void 0 : data[key];
        if (typeof value === 'string' && value.length > 0)
            return value;
    }
    return '';
}
async function issuerBase() {
    const cfg = await getConfig();
    return (cfg.humanAuthApiUrl || 'https://auth.bounded.sh').replace(/\/$/, '');
}
// Base64url-encode raw bytes via the platform abstraction (web: window.btoa;
// RN/Node: the polyfilled/Buffer btoa supplied by getPlatform()). On web this is
// byte-identical to the previous direct `btoa(...)` call.
function b64url(buf) {
    let s = '';
    const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
    for (let i = 0; i < bytes.length; i++)
        s += String.fromCharCode(bytes[i]);
    return getPlatform().btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
// PKCE verifier + S256 challenge, fully platform-neutral: random bytes and the
// SHA-256 digest both come from getPlatform() (web → WebCrypto, RN → polyfill/
// expo-crypto). Web output is identical to the previous raw-crypto path.
async function makePkce() {
    const rnd = getPlatform().getRandomBytes(32);
    const verifier = b64url(rnd);
    const digest = await getPlatform().sha256(getPlatform().textEncode(verifier));
    return { verifier, challenge: b64url(digest) };
}
function randomState() {
    return b64url(getPlatform().getRandomBytes(16));
}
function normalizeMethod(value) {
    const normalized = String(value || '').trim().toLowerCase();
    return /^[a-z0-9_-]{1,40}$/.test(normalized) ? normalized : null;
}
// The identifier-first hosted methods: the user types who they are and proves it,
// so a prompt-less authorize may still deliberately SSO a live issuer session.
// Every OTHER explicit provider jump is a social IdP button ("Continue with
// Google"), where silent SSO is the logged-out-then-same-account-again trap.
const IDENTIFIER_METHODS = new Set(['email', 'text', 'phone']);
function appendHostedLoginHints(params, opts) {
    const methods = (opts.methods || [])
        .map((method) => normalizeMethod(method))
        .filter((method) => !!method);
    if (methods.length > 0) {
        params.set('bounded_methods', Array.from(new Set(methods)).join(','));
    }
    const provider = normalizeMethod(opts.provider);
    if (provider)
        params.set('bounded_provider', provider);
    // login_hint: an identifier (usually an email) to prefill on the hosted page.
    // Kept short and free of control chars; the hosted page validates before use.
    const hint = String(opts.loginHint || '').trim();
    if (hint && hint.length <= 320 && !/[\s<>"']/.test(hint))
        params.set('login_hint', hint);
    // Standard OIDC prompt (e.g. 'login' to force a fresh sign-in / account choice).
    // An explicit social-provider jump defaults to 'select_account': the issuer
    // treats it as a demand for a fresh interactive sign-in (it never silently
    // SSOs a live issuer session under that prompt), and Google's leg always
    // carries its own select_account - so the user gets the IdP's account chooser
    // EVERY time, not just in a cookie-free browser. An explicit `prompt` always
    // wins, and `prompt: ''` opts a jump back into silent SSO.
    const prompt = opts.prompt === undefined
        ? (provider && !IDENTIFIER_METHODS.has(provider) ? 'select_account' : null)
        : normalizeMethod(opts.prompt);
    if (prompt)
        params.set('prompt', prompt);
}
// Build the authorize URL + the PKCE state that pairs with it. PURE: no storage,
// no navigation — so it's shared verbatim by the web redirect/popup paths and the
// native (expo-web-browser) path. The caller decides where the PkceState lives
// (web → sessionStorage across the full-page redirect; native → in-memory across
// the awaited browser session).
async function buildAuthorize(opts) {
    const cfg = await getConfig();
    if (!cfg.appId)
        throw new Error('appId is not configured (init the SDK with your appId)');
    // Web: default to the current page so callers don't have to pass redirectUri. Native has no
    // window — it MUST pass an https universal link (the system auth session needs a real URL).
    const redirectUri = opts.redirectUri || (hasDOM() ? window.location.origin + window.location.pathname : undefined);
    if (!redirectUri)
        throw new Error('redirectUri is required on React Native — pass the https universal link the app owner registered.');
    const { verifier, challenge } = await makePkce();
    const state = randomState();
    const base = await issuerBase();
    const params = new URLSearchParams({
        client_id: cfg.appId, redirect_uri: redirectUri, response_type: 'code', scope: 'openid profile email',
        code_challenge: challenge, code_challenge_method: 'S256', state,
    });
    appendHostedLoginHints(params, opts);
    return { url: `${base}/api/auth/oauth2/authorize?${params.toString()}`, pkce: { verifier, state, redirectUri } };
}
// Are we on React Native (no DOM)? OIDC dispatch uses this to choose the native
// expo-web-browser driver vs. the web full-page-redirect/popup driver.
function isNative() {
    return !getPlatform().hasDOM;
}
// ---------------------------------------------------------------------------
// Native (React Native / Expo) redirect driver
// ---------------------------------------------------------------------------
// Lazily load expo-web-browser. It's an OPTIONAL peer dep — imported dynamically
// so the web/Node bundle never requires it. Throws an actionable error if a native
// app calls hosted login without installing it.
async function loadExpoWebBrowser() {
    try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        return await import('expo-web-browser');
    }
    catch (_a) {
        throw new Error('React Native hosted login requires the optional peer dependency "expo-web-browser". ' +
            'Install it with `npx expo install expo-web-browser` (and `expo-crypto`), then retry.');
    }
}
// Parse code/state out of the universal-link callback URL. Hand-rolled (no `new URL()`)
// because React Native's URL implementation is non-spec-compliant for query parsing.
function parseCallbackParams(callbackUrl) {
    var _a, _b;
    const qIndex = callbackUrl.indexOf('?');
    if (qIndex < 0)
        return { code: null, state: null };
    const query = callbackUrl.slice(qIndex + 1).split('#')[0];
    const out = {};
    for (const part of query.split('&')) {
        if (!part)
            continue;
        const eq = part.indexOf('=');
        const k = eq < 0 ? part : part.slice(0, eq);
        const v = eq < 0 ? '' : part.slice(eq + 1);
        try {
            out[decodeURIComponent(k)] = decodeURIComponent(v.replace(/\+/g, ' '));
        }
        catch (_c) {
            out[k] = v;
        }
    }
    return { code: (_a = out['code']) !== null && _a !== void 0 ? _a : null, state: (_b = out['state']) !== null && _b !== void 0 ? _b : null };
}
// Full native login: open the hosted page in the system auth session, await the
// universal-link callback, verify state, and run the SAME PKCE code→token exchange
// as web. Returns the signed-in User (native completes inline — there's no separate
// completeLoginFromRedirect() round-trip the way the web full-page redirect needs).
async function runNativeLogin(opts) {
    var _a, _b;
    const { url, pkce } = await buildAuthorize(opts);
    const WebBrowser = await loadExpoWebBrowser();
    const openAuthSession = WebBrowser.openAuthSessionAsync || ((_a = WebBrowser.default) === null || _a === void 0 ? void 0 : _a.openAuthSessionAsync);
    if (typeof openAuthSession !== 'function') {
        throw new Error('expo-web-browser.openAuthSessionAsync is unavailable — update expo-web-browser.');
    }
    setAuthLoading(true);
    try {
        // openAuthSessionAsync keeps JS alive while the system browser is shown, then
        // resolves with the callback URL once the universal link fires — so `pkce`
        // stays in scope and never needs to be persisted.
        const result = await openAuthSession(url, opts.redirectUri);
        if (!result || result.type === 'cancel' || result.type === 'dismiss') {
            throw new Error('Login cancelled');
        }
        if (result.type !== 'success' || typeof result.url !== 'string') {
            throw new Error(`Hosted login did not complete (result: ${(_b = result === null || result === void 0 ? void 0 : result.type) !== null && _b !== void 0 ? _b : 'unknown'})`);
        }
        const { code, state: returnedState } = parseCallbackParams(result.url);
        if (!code)
            throw new Error('No authorization code in the callback URL');
        if (!returnedState || returnedState !== pkce.state) {
            throw new Error('OAuth state mismatch — possible CSRF; aborting');
        }
        return await exchangeCode(code, pkce.verifier, pkce.redirectUri);
    }
    finally {
        setAuthLoading(false);
    }
}
/** Start hosted login (Google / Apple / email / hosted phone). Works on WEB and REACT NATIVE.
 *
 *  WEB: navigates the whole page to the hosted Bounded login and resolves (void) as the page
 *  unloads; on return to `redirectUri?code=...` you call completeLoginFromRedirect().
 *
 *  REACT NATIVE: opens the hosted page via expo-web-browser, awaits the https universal-link
 *  callback, runs the PKCE exchange INLINE, and resolves with the signed-in User. No
 *  completeLoginFromRedirect() call is needed on native. Requires `redirectUri` to be an
 *  https UNIVERSAL LINK whose origin the app owner registered in the app's allowedOrigins
 *  (custom `myapp://` schemes are rejected by the issuer). Needs the optional peer deps
 *  expo-web-browser + (expo-crypto or react-native-get-random-values). */
export async function loginWithRedirect(opts) {
    if (isNative())
        return runNativeLogin(opts);
    if (!hasDOM())
        throw new Error('loginWithRedirect requires a browser or a configured React Native platform');
    const { url, pkce } = await buildAuthorize(opts);
    // Stash the verifier + state so the redirect_uri page can finish the exchange. sessionStorage
    // is per-tab and cleared on close — appropriate for a short-lived auth handshake. Keyed by
    // this attempt's own state so a second concurrent login cannot overwrite it.
    sessionStorage.setItem(pkceKey(pkce.state), JSON.stringify(pkce));
    setAuthLoading(true); // reflect "signing in…" right up to navigation; the return page's
    window.location.href = url; // completeLoginFromRedirect() keeps it true until the user resolves.
}
/** Finish login on the redirect_uri page (WEB ONLY): exchange ?code= for a token (PKCE-verified),
 *  store the session, and return the User. Idempotent: returns null if there's no ?code= in the URL.
 *
 *  On REACT NATIVE this returns null and is a no-op: native login completes INLINE inside
 *  loginWithRedirect() (which resolves with the User), so there is no separate redirect
 *  page to finish from. Calling it on RN is harmless. */
export async function completeLoginFromRedirect() {
    if (!hasDOM())
        return null;
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const returnedState = url.searchParams.get('state');
    if (!code)
        return null;
    // POPUP path: if we're the redirect_uri page running INSIDE the loginWithPopup() window,
    // hand the code to the opener and close — no separate completeLoginInPopup() call needed.
    // The opener is the same app/origin, so post to our own origin (the opener's message
    // handler checks ev.origin === redirect_uri origin). One call now finishes BOTH flows.
    if (window.opener && window.opener !== window) {
        try {
            window.opener.postMessage({ type: 'bounded-oidc-code', code, state: returnedState }, window.location.origin);
        }
        catch ( /* ignore */_a) { /* ignore */ }
        try {
            window.close();
        }
        catch ( /* ignore */_b) { /* ignore */ }
        return null;
    }
    setAuthLoading(true);
    try {
        // Read back the exact record THIS attempt wrote, keyed by the state the issuer
        // returned, so a concurrent login's record is never consumed by mistake.
        const raw = returnedState ? sessionStorage.getItem(pkceKey(returnedState)) : null;
        if (!raw)
            throw new Error('Missing PKCE state — start login with loginWithRedirect()');
        const pkce = JSON.parse(raw);
        if (!returnedState || returnedState !== pkce.state)
            throw new Error('OAuth state mismatch — possible CSRF; aborting');
        const user = await exchangeCode(code, pkce.verifier, pkce.redirectUri);
        sessionStorage.removeItem(pkceKey(returnedState));
        // Strip code/state from the URL so a reload doesn't re-exchange (codes are single-use anyway).
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
        return user;
    }
    finally {
        setAuthLoading(false);
    }
}
/** Popup variant: open the hosted login in a popup; resolves with the User once the popup's
 *  redirect_uri page posts the code back. The redirect_uri page just calls the usual
 *  completeLoginFromRedirect() (which auto-detects the popup) — no separate call needed. */
export async function loginWithPopup(opts) {
    if (!hasDOM())
        throw new Error('loginWithPopup is web-only — on React Native use loginWithRedirect()');
    // Open synchronously, before PKCE/config awaits consume the click's user-
    // activation window. Opening only after buildAuthorize() intermittently
    // left Chrome with a real named window stranded at about:blank (or blocked
    // it entirely), while callers waited forever for an OAuth message.
    const w = opts.width || 480, h = opts.height || 720;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    // Per-attempt window name: a FIXED name made a second concurrent open() reuse and
    // renavigate the first popup's browsing context. A fresh random name isolates each.
    const popupName = `bounded-oidc-${randomState()}`;
    const popup = window.open('', popupName, `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup)
        throw new Error('Popup blocked — use loginWithRedirect() instead');
    let authorization;
    try {
        authorization = await buildAuthorize(opts);
    }
    catch (error) {
        try {
            popup.close();
        }
        catch ( /* ignore */_a) { /* ignore */ }
        throw error;
    }
    const { url, pkce } = authorization;
    // Validate every remaining synchronous setup step before navigating. Both
    // URL parsing and sessionStorage can throw (malformed caller input, blocked
    // storage, quota/privacy mode); because the popup is already open to retain
    // user activation, close it on either failure instead of orphaning a blank
    // window with no listener or watchdog attached.
    let expectedOrigin;
    try {
        expectedOrigin = new URL(pkce.redirectUri).origin;
        sessionStorage.setItem(pkceKey(pkce.state), JSON.stringify(pkce));
    }
    catch (error) {
        try {
            popup.close();
        }
        catch ( /* ignore */_b) { /* ignore */ }
        throw error;
    }
    try {
        popup.location.replace(url);
    }
    catch (_c) {
        try {
            popup.location.href = url;
        }
        catch (_d) {
            try {
                popup.close();
            }
            catch ( /* ignore */_e) { /* ignore */ }
            throw new Error('Popup failed to navigate — use loginWithRedirect() instead');
        }
    }
    // expectedOrigin came from the RESOLVED redirect_uri (buildAuthorize defaulted it to the
    // current page on web), not opts.redirectUri which may be undefined.
    setAuthLoading(true); // reflect "signing in…" while the popup is open
    try {
        return await new Promise((resolve, reject) => {
            let settled = false;
            let timer;
            let navigationTimer;
            const cleanup = () => {
                settled = true;
                clearInterval(timer);
                clearTimeout(navigationTimer);
                window.removeEventListener('message', onMsg);
            };
            const onMsg = async (ev) => {
                if (ev.origin !== expectedOrigin)
                    return; // only trust the redirect_uri origin
                const d = ev.data;
                if (!d || d.type !== 'bounded-oidc-code' || typeof d.code !== 'string')
                    return;
                // Validate state BEFORE tearing down this listener, against the record THIS
                // attempt captured in the closure (`pkce`), not a shared sessionStorage reread.
                // A message from an OVERLAPPING attempt carries a different state: ignore it and
                // stay registered for our own code, instead of cleaning up on another's message.
                if (d.state !== pkce.state)
                    return;
                cleanup();
                try {
                    const user = await exchangeCode(d.code, pkce.verifier, pkce.redirectUri);
                    sessionStorage.removeItem(pkceKey(pkce.state));
                    try {
                        popup.close();
                    }
                    catch ( /* ignore */_a) { /* ignore */ }
                    resolve(user);
                }
                catch (e) {
                    reject(e);
                }
            };
            window.addEventListener('message', onMsg);
            // Some browser/extension combinations can return a real popup handle but
            // strand the window at about:blank instead of navigating to the hosted
            // authorize URL. The old code then waited forever because the popup was
            // open and no message could ever arrive. Once the popup becomes
            // cross-origin, reading location throws — that is positive evidence that
            // navigation succeeded, so only a still-readable about:blank is rejected.
            //
            // F-006: the old single 4s check killed HEALTHY logins. `about:blank` is
            // also what a PENDING navigation reads while the browser waits for the
            // authorize response - measured in prod at 2.2-3.7s server-side plus
            // network, i.e. straddling 4s - so the watchdog repeatedly closed popups
            // whose login was succeeding ("popup opened and closed itself"). A truly
            // stranded popup is just as detectable later; a slow-but-working one is
            // only survivable with patience. Recheck instead of kill: give up only
            // when the window is STILL readable-blank after the full grace.
            const NAVIGATION_GRACE_MS = 30000;
            const NAVIGATION_RECHECK_MS = 2000;
            const navigationStartedAt = Date.now();
            const checkNavigation = () => {
                if (settled || popup.closed)
                    return;
                try {
                    if (popup.location.href === 'about:blank') {
                        if (Date.now() - navigationStartedAt >= NAVIGATION_GRACE_MS) {
                            cleanup();
                            try {
                                popup.close();
                            }
                            catch ( /* ignore */_a) { /* ignore */ }
                            reject(new Error('Popup stalled at about:blank - use loginWithRedirect() instead'));
                            return;
                        }
                        navigationTimer = setTimeout(checkNavigation, NAVIGATION_RECHECK_MS);
                    }
                }
                catch ( /* cross-origin hosted auth loaded normally */_b) { /* cross-origin hosted auth loaded normally */ }
            };
            navigationTimer = setTimeout(checkNavigation, NAVIGATION_RECHECK_MS);
            // The popup posts its code and then closes ITSELF, so a naive "popup.closed -> reject"
            // races the just-posted message and would report a spurious "Login window closed" on a
            // SUCCESSFUL login. When we see it closed, wait a beat for that pending message to settle
            // before declaring the window closed.
            timer = setInterval(() => {
                if (popup.closed) {
                    clearInterval(timer);
                    setTimeout(() => { if (!settled) {
                        cleanup();
                        reject(new Error('Login window closed'));
                    } }, 500);
                }
            }, 500);
        });
    }
    finally {
        setAuthLoading(false);
    }
}
/** Call this on the redirect_uri page when login was opened via loginWithPopup(): posts the
 *  code back to the opener and closes. (For the redirect variant use completeLoginFromRedirect.) */
export function completeLoginInPopup(openerOrigin) {
    if (!hasDOM() || !window.opener)
        return;
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    if (!code)
        return;
    try {
        window.opener.postMessage({ type: 'bounded-oidc-code', code, state }, openerOrigin);
    }
    catch ( /* ignore */_a) { /* ignore */ }
    try {
        window.close();
    }
    catch ( /* ignore */_b) { /* ignore */ }
}
// Establish a Bounded session from a server-minted inline-session response (the same
// { idToken, accessToken, refreshToken } shape as /session/refresh). Used by the
// Turnkey-native login path (authMode:"turnkey"), which mints the session server-side
// after Turnkey verifies the email OTP - there is no OIDC code to exchange. Wiring is
// identical to exchangeCode's tail so the realtime worker cannot tell them apart.
export async function establishInlineSession(data) {
    const base = await issuerBase();
    const idToken = tokenField(data, 'id_token', 'idToken');
    if (!idToken)
        throw new Error('No id_token in the inline session response');
    const accessToken = tokenField(data, 'access_token', 'accessToken') || idToken;
    const refreshToken = boundedRefreshTokenField(data);
    const user = userFromIdToken(idToken);
    await sessionManager().storeSession(String(user.address || ''), accessToken, idToken, refreshToken, base);
    // Adopt the OIDC provider on EVERY session surface - including core's signing
    // pointer (getConfig().authProvider), which this path used to skip, leaving
    // onchain writes routed through a previous (e.g. logged-out wallet) provider.
    await adoptSessionProvider({ provider: oidcProviderSingleton, method: 'email', configKey: 'direct:oidc', user });
    // Turnkey-native OTP mints an app session directly. It does not establish a
    // Better Auth browser cookie on the issuer, so logout must not navigate
    // through the issuer's cookie-clearing /logout endpoint. Stamped AFTER the
    // adoption: setStoredAuthMethod clears the kind marker for non-email methods,
    // and the adopt call is what records 'email'.
    try {
        getPlatform().storage.setItem(ISSUER_SESSION_KIND_KEY, 'inline');
    }
    catch ( /* storage unavailable */_a) { /* storage unavailable */ }
    return user;
}
// Auth base + appId for the Turnkey-native inline OTP endpoints, resolved through the
// same config the OIDC flow uses.
export async function inlineAuthEndpoint() {
    const cfg = await getConfig();
    return { base: await issuerBase(), appId: cfg.appId };
}
async function exchangeCode(code, verifier, redirectUri) {
    const cfg = await getConfig();
    const base = await issuerBase();
    // OIDC token endpoint: application/x-www-form-urlencoded (the standard). Public client (no
    // secret) authenticated by PKCE. The code is single-use + short-TTL on the server.
    const body = new URLSearchParams({
        grant_type: 'authorization_code', code, redirect_uri: redirectUri,
        client_id: cfg.appId, code_verifier: verifier,
    });
    let res;
    try {
        res = await fetch(`${base}/api/auth/oauth2/token`, {
            method: 'POST', headers: { 'content-type': 'application/x-www-form-urlencoded' }, body: body.toString(),
        });
    }
    catch (err) {
        throw new Error(`Bounded token exchange failed: ${(err === null || err === void 0 ? void 0 : err.message) || err}`);
    }
    let data = null;
    try {
        data = await res.json();
    }
    catch ( /* non-JSON */_a) { /* non-JSON */ }
    if (!res.ok)
        throw new Error(String((data && (data.error_description || data.error || data.message)) || `token HTTP ${res.status}`));
    const idToken = (data === null || data === void 0 ? void 0 : data.id_token) || (data === null || data === void 0 ? void 0 : data.idToken);
    if (!idToken)
        throw new Error('No id_token returned from the token endpoint');
    const accessToken = tokenField(data, 'access_token', 'accessToken') || idToken;
    const refreshToken = boundedRefreshTokenField(data);
    const user = userFromIdToken(idToken);
    await sessionManager().storeSession(String(user.address || ''), accessToken, idToken, refreshToken, base);
    // Adopt on EVERY session surface (see establishInlineSession) - hosted
    // popup/redirect completions all land here, so this closes their gap too.
    await adoptSessionProvider({ provider: oidcProviderSingleton, method: 'email', configKey: 'direct:oidc', user });
    // OIDC login does establish the issuer's Better Auth browser cookie. Record
    // that fact separately from the provider name so logout clears the cookie
    // and returns through the registered app origin. Stamped AFTER the adoption
    // (setStoredAuthMethod clears the kind marker for non-email methods).
    try {
        getPlatform().storage.setItem(ISSUER_SESSION_KIND_KEY, 'hosted');
    }
    catch ( /* storage unavailable */_b) { /* storage unavailable */ }
    return user;
}
